import { useEffect, useMemo } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Info } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { Modal } from '@/components/modal';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ButtonLoadable } from '@/components/ui/button-loadable';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

import { useCreateUserGroup } from '../../hooks/user-groups/use-create-user-group';
import { useRenameUserGroup } from '../../hooks/user-groups/use-rename-user-group';
import { UserGroupPermissions } from '../../types/permission';
import { UserGroupFormMode } from '../../types/user-group-form-mode';
import {
  buildDuplicateGroupName,
  resolveDuplicateGroupPolicies,
} from '../../utils/user-group.utils';
import {
  USER_GROUP_NAME_MAX_LENGTH,
  userGroupFormSchema,
  UserGroupFormValues,
} from '../../validations/user-group-validation';

const FORM_FIELD_NAME = 'name';

const I18N = 'authenticated.settings:pages.permissions.groupModal';

const DUPLICATE_NAME_ERROR_KEY = `${I18N}.errors.duplicateName`;

const MODE_TITLE_KEYS: Record<UserGroupFormMode, string> = {
  [UserGroupFormMode.Create]: `${I18N}.createTitle`,
  [UserGroupFormMode.Rename]: `${I18N}.renameTitle`,
  [UserGroupFormMode.Duplicate]: `${I18N}.duplicateTitle`,
};

const MODE_SUBMIT_KEYS: Record<UserGroupFormMode, string> = {
  [UserGroupFormMode.Create]: `${I18N}.createButton`,
  [UserGroupFormMode.Rename]: 'common:save',
  [UserGroupFormMode.Duplicate]: `${I18N}.duplicateButton`,
};

interface UserGroupFormModalProps {
  open: boolean;
  onClose: () => void;
  mode: UserGroupFormMode;
  /** Group being renamed or duplicated; null in create mode. */
  group: UserGroupPermissions | null;
  /** Loaded groups — used for the duplicate name suggestion and the duplicate-name check. */
  existingGroups: UserGroupPermissions[];
}

/**
 * Create / rename / duplicate modal for a user group.
 *
 * All three endpoints save immediately (they are not part of the permissions matrix's
 * save-on-footer flow), so the modal owns its mutations and closes on success.
 * Create sends no policies — the new column starts empty. Duplicate seeds the new group with
 * the source group's policies, so its column opens with the same boxes ticked.
 */
export const UserGroupFormModal = ({
  open,
  onClose,
  mode,
  group,
  existingGroups,
}: UserGroupFormModalProps) => {
  const { t } = useTranslation(['authenticated.settings', 'common']);

  const form = useForm<UserGroupFormValues>({
    resolver: zodResolver(userGroupFormSchema),
    mode: 'onChange',
    defaultValues: { [FORM_FIELD_NAME]: '' },
  });

  // "<name> copy", falling back to "<name> copy 2", 3, … so the modal never opens on an error
  const suggestedDuplicateName = useMemo(() => {
    if (mode !== UserGroupFormMode.Duplicate || !group) return '';

    return buildDuplicateGroupName({
      existingNames: existingGroups.map(existing => existing.name),
      formatCandidate: copyIndex =>
        copyIndex === 0
          ? t(`${I18N}.duplicateNameSuffix`, { name: group.name })
          : t(`${I18N}.duplicateNameSuffixNumbered`, { name: group.name, count: copyIndex + 1 }),
    });
  }, [mode, group, existingGroups, t]);

  // Re-seed each time the modal opens for a different group / mode
  useEffect(() => {
    if (!open) return;

    const initialName: Record<UserGroupFormMode, string> = {
      [UserGroupFormMode.Create]: '',
      [UserGroupFormMode.Rename]: group?.name ?? '',
      [UserGroupFormMode.Duplicate]: suggestedDuplicateName,
    };

    form.reset({ [FORM_FIELD_NAME]: initialName[mode] });
  }, [open, mode, group, suggestedDuplicateName, form]);

  const createMutation = useCreateUserGroup({ onSuccess: onClose });
  const renameMutation = useRenameUserGroup({ onSuccess: onClose });
  const isPending = createMutation.isPending || renameMutation.isPending;

  const handleSubmit = form.handleSubmit(values => {
    const name = values.name.trim();

    // There is no dedicated backend code for a duplicate group name, so the check is
    // client-side. A lost race still surfaces through the mutation's error toast.
    // Only a rename may keep its own name — a duplicate must not reuse the source's.
    const isDuplicateName = existingGroups.some(
      existing =>
        !(mode === UserGroupFormMode.Rename && existing.id === group?.id) &&
        existing.name.trim().toLowerCase() === name.toLowerCase()
    );

    if (isDuplicateName) {
      form.setError(FORM_FIELD_NAME, { message: DUPLICATE_NAME_ERROR_KEY });
      return;
    }

    if (mode === UserGroupFormMode.Rename && group) {
      renameMutation.mutate({ id: group.id, name });
      return;
    }

    if (mode === UserGroupFormMode.Duplicate && group) {
      createMutation.mutate({ name, policies: resolveDuplicateGroupPolicies(group) });
      return;
    }

    createMutation.mutate({ name });
  });

  return (
    <Modal
      open={open}
      onOpen={() => {}}
      onClose={onClose}
      preventClose={isPending}
      className="max-w-md"
    >
      <Modal.Title>{t(MODE_TITLE_KEYS[mode])}</Modal.Title>

      <Form {...form}>
        <form onSubmit={handleSubmit} data-1p-ignore autoComplete="off">
          <Modal.Body>
            {mode === UserGroupFormMode.Duplicate && group && (
              <Alert variant="info">
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <Trans
                    t={t}
                    i18nKey={`${I18N}.duplicateInfo`}
                    values={{ name: group.name }}
                    components={{ strong: <strong className="font-semibold" /> }}
                  />
                </AlertDescription>
              </Alert>
            )}

            <FormField
              control={form.control}
              name={FORM_FIELD_NAME}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t(`${I18N}.nameLabel`)}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      maxLength={USER_GROUP_NAME_MAX_LENGTH}
                      placeholder={t(`${I18N}.namePlaceholder`)}
                      autoComplete="off"
                      data-1p-ignore
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Modal.Body>

          <Modal.Footer>
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              {t('common:cancel')}
            </Button>
            <ButtonLoadable type="submit" isLoading={isPending} disabled={!form.formState.isValid}>
              {t(MODE_SUBMIT_KEYS[mode])}
            </ButtonLoadable>
          </Modal.Footer>
        </form>
      </Form>
    </Modal>
  );
};
